import React, { memo } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Palette, BookHeart, Puzzle, Bookmark, PencilRuler, Sparkles } from 'lucide-react';
interface CollectionCardProps {
  number: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
}
function CollectionCard({
  number,
  icon,
  iconBg,
  iconColor,
  title,
  description
}: CollectionCardProps) {
  return <div className="bg-white rounded-xl p-4 shadow-sm h-full flex flex-col transition-transform duration-300 hover:-translate-y-1" style={{
    borderColor: 'var(--color-border)',
    borderWidth: '1px'
  }} data-id="element-1">
      <div className="flex items-center gap-3 mb-2" data-id="element-2">
        <span className="text-sm font-bold opacity-30" style={{
        color: 'var(--color-deep-blue)'
      }} data-id="element-3">
          {number}
        </span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
        backgroundColor: iconBg
      }} data-id="element-4">
          <span style={{
          color: iconColor
        }} data-id="element-5">
            {icon}
          </span>
        </div>
        <h3 className="text-base font-bold" style={{
        color: 'var(--color-deep-blue)'
      }} data-id="element-6">
          {title}
        </h3>
      </div>
      <p className="text-sm leading-relaxed" style={{
      color: 'var(--color-body)'
    }} data-id="element-7">
        {description}
      </p>
    </div>;
}
export function CollectionSection() {
  return <section id="coleccion" className="pt-[40px] pb-[40px]" style={{
    backgroundColor: 'var(--color-off-white)'
  }} data-id="element-8">
      <div className="container mx-auto px-4" data-id="element-9">
        <FadeIn data-id="element-10">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6" style={{
          color: 'var(--color-deep-blue)'
        }} data-id="element-11">
            Todo lo que incluye la colección
          </h2>
        </FadeIn>

        <div className="max-w-5xl mx-auto mb-6" data-id="element-12">
          <FadeIn delay={0} data-id="element-13">
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-md border-2 transition-transform duration-300 hover:-translate-y-1" style={{
            borderColor: 'var(--color-gold)'
          }} data-id="element-14">
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6" data-id="element-15">
                <div className="flex items-center gap-4 mb-2 md:mb-0 md:w-1/3" data-id="element-16">
                  <span className="text-2xl font-black opacity-20" style={{
                  color: 'var(--color-gold)'
                }} data-id="element-17">
                    01
                  </span>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: '#FEF6E8'
                }} data-id="element-18">
                    <BookHeart className="w-6 h-6" style={{
                    color: 'var(--color-gold)'
                  }} data-id="element-19" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold leading-tight" style={{
                  color: 'var(--color-deep-blue)'
                }} data-id="element-20">
                    10 libros de parábolas para niños
                  </h3>
                </div>
                <div className="md:w-2/3" data-id="element-21">
                  <p className="text-base md:text-lg leading-relaxed mb-4" style={{
                  color: 'var(--color-body)'
                }} data-id="element-22">
                    Historias organizadas para acompañar a los niños en
                    diferentes momentos, con enseñanzas y preguntas que ayudan a
                    reflexionar, aprender y crecer en la fe.
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-23">
                    {['Distintas temáticas para diferentes momentos', 'Enseñanzas claras y adaptadas para niños', 'Preguntas para reflexionar y conversar', 'Ideal para familia, iglesia o aula'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-24">
                        <Sparkles className="w-4 h-4 mt-1 flex-shrink-0" style={{
                      color: 'var(--color-gold)'
                    }} data-id="element-25" />
                        <span className="text-sm font-medium" style={{
                      color: 'var(--color-deep-blue)'
                    }} data-id="element-26">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-27">
          <FadeIn delay={0.08} data-id="element-28">
            <CollectionCard number="02" icon={<Palette className="w-5 h-5" data-id="element-30" />} iconBg="#EAF2F8" iconColor="var(--color-soft-blue)" title="Biblia para colorear" description="Ilustraciones bíblicas pensadas para que los niños aprendan mientras colorean." data-id="element-29" />
          </FadeIn>
          <FadeIn delay={0.16} data-id="element-31">
            <CollectionCard number="03" icon={<BookHeart className="w-5 h-5" data-id="element-33" />} iconBg="#FDF2EC" iconColor="var(--color-soft-peach)" title="Devocional familiar" description="Reflexiones simples para compartir momentos con Dios en familia." data-id="element-32" />
          </FadeIn>
          <FadeIn delay={0.24} data-id="element-34">
            <CollectionCard number="04" icon={<Puzzle className="w-5 h-5" data-id="element-36" />} iconBg="#F0F7F2" iconColor="var(--color-green)" title="Juego cristiano imprimible" description="Recurso divertido para enseñar valores y símbolos cristianos jugando." data-id="element-35" />
          </FadeIn>
          <FadeIn delay={0.32} data-id="element-37">
            <CollectionCard number="05" icon={<Bookmark className="w-5 h-5" data-id="element-39" />} iconBg="#FEF6E8" iconColor="var(--color-gold)" title="Versículos para memorizar" description="Selección de versículos fáciles y significativos para niños." data-id="element-38" />
          </FadeIn>
          <FadeIn delay={0.4} data-id="element-40">
            <CollectionCard number="06" icon={<PencilRuler className="w-5 h-5" data-id="element-42" />} iconBg="#EEF0F8" iconColor="#7B8EC4" title="Actividades cristianas para niños" description="Ejercicios y dinámicas para reforzar enseñanzas bíblicas." data-id="element-41" />
          </FadeIn>
          <FadeIn delay={0.48} data-id="element-43">
            <CollectionCard number="07" icon={<Sparkles className="w-5 h-5" data-id="element-45" />} iconBg="#FDF8F2" iconColor="var(--color-gold)" title="Cuaderno de oración infantil" description="Espacio guiado para que los niños expresen sus oraciones a Dios." data-id="element-44" />
          </FadeIn>
        </div>

        <FadeIn delay={0.56} data-id="element-46">
          <div className="flex justify-center mt-8" data-id="element-47">
            <a href="#comprar" className="inline-flex items-center justify-center px-12 py-4 text-lg font-bold text-white rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" style={{
            backgroundColor: '#2563EB',
            boxShadow: '0 4px 20px rgba(37,99,235,0.35)'
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#1D4ED8';
            e.currentTarget.style.boxShadow = '0 6px 25px rgba(37,99,235,0.5)';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#2563EB';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.35)';
          }} data-id="element-48">
              OBTENER AHORA
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
}